import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./PostPurchaseSetup.module.css";
import axios from "axios";
import {
  getAgentById,
  isSyncContactSkip,
  calenderCheck,
  fetchAvailablePhoneNumberByCountry,
  updateAgent,
  API_BASE_URL,
} from "../../Store/apiStore";
import { useAgentStore } from "../../Store/agentDetailStore";
import decodeToken from "../../lib/decodeToken";
import { markSetupStep } from "../../lib/setupScore";
import { isAppSumoUser, isPythonBackend, usesVobiz } from "../../utils/apiBackend";

// Step components reused across PostPurchaseSetup + GoLiveWizard so
// the two wizards' UI/UX stay identical wherever the screens overlap.
// Number+Ownership is merged into a single ImmediateOwnershipStep
// running in `wizardInlinePicker` mode (same as GoLive Step 1) so the
// user picks/confirms a number on one screen instead of two. Forward
// + Triage import directly from GoLiveWizard/steps so the post-spec
// patterns (SophiaReadyDialog completion, "I will publish directly /
// Skip this step" footer, etc.) drop in unchanged.
import GoogleAuthStep from "./steps/GoogleAuthStep";
import PersonalDetailsStep from "./steps/PersonalDetailsStep";
import BusinessVerifyStep from "./steps/BusinessVerifyStep";
import ImmediateOwnershipStep from "./steps/ImmediateOwnershipStep";
import CarrierSelectStep from "./steps/CarrierSelectStep";
import ForwardingStep from "../GoLiveWizard/steps/ForwardingStep";
import TriageStep from "../GoLiveWizard/steps/TriageStep";
import ContactSyncStep from "../ContactSyncStep/ContactSyncStep";
import useStepBackGuard from "../../hooks/useStepBackGuard";
import ExitConfirmModal from "../ExitConfirmModal/ExitConfirmModal";

/*
 * PostPurchaseSetup — Stripe-checkout onboarding wizard.
 *
 * Flow rewritten to mirror GoLiveWizard wherever the screens overlap,
 * so the UI / setup-score firing / copy stay consistent across both
 * onboarding entry points. Differences from GoLive that justify a
 * separate wizard:
 *   - Step 1 = Google Auth at the START (mandatory grant before
 *     anything else). GoLive puts Google at the END after Triage.
 *   - Step 2 = Personal Details (CONDITIONAL — only when the
 *     Thankyou page passed `?personalDetails=true` in the URL).
 *   - Step 3 = Business Verify / GMB (paid users haven't filled
 *     business details during checkout — GoLive users have via the
 *     free-signup BusinessStep).
 *
 * Steps 4-7 share components / patterns with GoLive directly:
 *   4. Number+Ownership (ImmediateOwnershipStep `wizardInlinePicker`)
 *   5. Carrier Select (CarrierSelectStep `wizardSkipPublishDirectly`)
 *   6. Forwarding (GoLive's ForwardingStep — Skip-publish footer)
 *   7. Triage (GoLive's TriageStep — opens SophiaReadyDialog at end)
 *
 * Triage's SophiaReadyDialog drives the post-wizard fork:
 *   - Continue Setup → render <ContactSyncStep nextRoute="/fast-agent-detail"/>
 *     inline → /fast-agent-detail
 *   - Go to Dashboard → /fast-agent-detail directly (skip contact-sync)
 *
 * Route guard: visitor needs `paymentDone=true` in localStorage (set
 * by PostPurchaseThankyou) OR returning Google flag OR a saved step.
 *
 * Resume: each step writes progress to localStorage under
 * `postPurchaseSetup.lastStep`. Per spec there are no existing users
 * mid-wizard at the time of this rewrite, so the new step IDs don't
 * collide with stale resumes.
 */

const STEPS = [
  { id: 1, key: "google",          label: "Connect Google",     Component: GoogleAuthStep },
  { id: 2, key: "personal",        label: "Personal Details",   Component: PersonalDetailsStep },
  { id: 3, key: "business",        label: "Verify Business",    Component: BusinessVerifyStep },
  { id: 4, key: "ownership",       label: "Assign Number",      Component: ImmediateOwnershipStep },
  { id: 5, key: "carrier",         label: "Phone Carrier",      Component: CarrierSelectStep },
  { id: 6, key: "forward",         label: "Forwarding Setup",   Component: ForwardingStep },
  { id: 7, key: "triage",          label: "Call Triage",        Component: TriageStep },
];

const STORAGE_KEY = "postPurchaseSetup.lastStep";

// Auto-assign supported countries — mirrors the GoLiveWizard logic.
// Non-US/CA/IN users fall through to the manual picker on Step 4.
const AUTO_ASSIGN_COUNTRIES = new Set(["US", "CA", "IN"]);

const IN_DEFAULT_LOCATION = {
  city: "Bangalore",
  stateCode: "KA",
  country: "IN",
};

export default function PostPurchaseSetup() {
  const navigate = useNavigate();
  const [stepId, setStepId] = useState(1);
  const [bootChecked, setBootChecked] = useState(false);
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState("");
  const [connectedScopes, setConnectedScopes] = useState("");
  const [justReturnedFromGoogle, setJustReturnedFromGoogle] = useState(false);

  /*
   * Conditional Personal Details inclusion. Two signals (either is
   * sufficient — belt-and-braces):
   *   1. URL query `?personalDetails=true` stamped by Thankyou's
   *      navigate target (`postPurchaseTarget`).
   *   2. sessionStorage `postPurchaseSetup.showPersonalDetails=true`
   *      stashed by Thankyou on mount, so an intermediate redirect
   *      stripping the query param can't drop the flag.
   *
   * Read once at mount and remembered in state — subsequent URL
   * cleanups (e.g. stripping `?googleAuth=success` after OAuth
   * round-trip) won't toggle the inclusion mid-wizard.
   */
  const [showPersonalDetails] = useState(() => {
    try {
      const fromUrl =
        new URLSearchParams(window.location.search).get(
          "personalDetails",
        ) === "true";
      const fromStorage =
        sessionStorage.getItem("postPurchaseSetup.showPersonalDetails") ===
        "true";
      const result = fromUrl || fromStorage;
      console.log("[PostPurchaseSetup DEBUG] showPersonalDetails check", {
        url: window.location.href,
        urlSearch: window.location.search,
        fromUrl,
        fromStorage,
        result,
      });
      return result;
    } catch (e) {
      console.warn(
        "[PostPurchaseSetup DEBUG] showPersonalDetails check threw:",
        e,
      );
      return false;
    }
  });

  /*
   * IMPORTANT: do NOT clear `postPurchaseSetup.showPersonalDetails`
   * on first mount.
   *
   * The Google OAuth round-trip (Step 1 → Google → callback redirect
   * to `/post-purchase-setup?googleAuth=success`) RE-MOUNTS this
   * component with a brand-new state. The URL also loses our original
   * `?personalDetails=true` param because Google only redirects to
   * the bare `returnTo` path. So on the second mount, the only
   * remaining signal is sessionStorage. Clearing it on first mount
   * was the bug — it killed the flag before OAuth could come back.
   *
   * Cleanup happens later via `useEffect` keyed off stepId moving
   * past the Personal Details position (effectively "user has seen
   * Personal Details, no need to remember the trigger flag").
   */
  useEffect(() => {
    if (!showPersonalDetails) return;
    if (!bootChecked) return;
    // Once the user has advanced past Personal Details (stepId > 2),
    // we're done with the trigger flag. Safe to clear.
    if (stepId > 2) {
      try {
        sessionStorage.removeItem("postPurchaseSetup.showPersonalDetails");
        console.log(
          "[PostPurchaseSetup DEBUG] Personal Details flag consumed — cleared sessionStorage",
        );
      } catch { }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId, bootChecked]);

  /*
   * Effective STEPS — Step 2 (Personal Details) is conditional. Built
   * once via useState so its identity is stable across renders (avoids
   * re-running goNext skip logic).
   */
  const [effectiveSteps] = useState(() => {
    const filtered = STEPS.filter(
      (s) => s.key !== "personal" || showPersonalDetails,
    );
    console.log(
      "[PostPurchaseSetup DEBUG] effectiveSteps =",
      filtered.map((s) => `${s.id}:${s.key}`).join(" → "),
      "| showPersonalDetails =",
      showPersonalDetails,
    );
    return filtered;
  });

  /*
   * Cascade auto-assign + candidate flow (mirrors GoLiveWizard).
   * Runs at mount once we have agent + business; stashes a
   * Telnyx-fetched candidate without placing the order. The actual
   * purchase fires only when the user taps Confirm on Step 4
   * (ImmediateOwnershipStep with `wizardInlinePicker`).
   */
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const autoAssignTriedRef = useRef(null);
  const [candidate, setCandidate] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [editConsumed, setEditConsumed] = useState(false);

  /*
   * Post-wizard ContactSync screen — replaces the legacy "navigate to
   * /contact-sync" handoff. After Triage's SophiaReadyDialog Continue
   * tap we render ContactSyncStep INLINE inside this wizard so the
   * user never leaves /post-purchase-setup, then ContactSyncStep's
   * own Continue/Skip lands them on /fast-agent-detail.
   */
  const [showContactSync, setShowContactSync] = useState(false);

  // ── Step-wise back guard ─────────────────────────────────────────
  // Device/browser back walks the wizard steps backward instead of
  // leaving the route (and never reaches the expired Stripe page). At
  // the first step (or the final inline contact-sync), Back asks for
  // exit confirmation → dashboard.
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  useStepBackGuard({
    // Arm immediately on mount (not gated on boot) so back can never
    // slip through to the expired Stripe page during the brief boot.
    active: true,
    canGoBack: () => {
      if (showContactSync) return false; // final phase — basic: confirm exit
      const idx = effectiveSteps.findIndex((s) => s.id === stepId);
      return idx > 0;
    },
    onBack: () => {
      const idx = effectiveSteps.findIndex((s) => s.id === stepId);
      if (idx > 0) setStepId(effectiveSteps[idx - 1].id);
    },
    onExit: () => setExitConfirmOpen(true),
  });

  // Agent context — loaded once at boot from the agent_id Thankyou.jsx
  // stashed in localStorage. Passed down to every step component so each
  // one can render personalized copy (business name) and call the right
  // backend endpoints without re-fetching.
  const [agentCtx, setAgentCtx] = useState({
    agentId: null,
    agent: null,
    business: null,
    loading: true,
  });

  const refreshAgentCtx = async () => {
    const agentId =
      localStorage.getItem("postPurchaseAgentId") ||
      sessionStorage.getItem("SelectAgentId") ||
      new URLSearchParams(window.location.search).get("agentId") ||
      null;

    if (!agentId) {
      setAgentCtx((c) => ({ ...c, loading: false }));
      return;
    }

    try {
      // Single fetch — getAgentById now eager-loads the `business`
      // association on the backend, so one round-trip gives us both
      // agent + business. (Earlier this needed a second call to
      // getBusinessDetailsByBusinessId on top.)
      const res = await getAgentById(agentId);
      const agent = res?.data || res || {};
      const business = agent?.business || null;
      const businessId = agent?.businessId;

      if (agent?.agent_id) sessionStorage.setItem("SelectAgentId", agent.agent_id);
      if (businessId) sessionStorage.setItem("SelectAgentBusinessId", businessId);

      // ✅ Prime the sync memory for CallTransfer if not already set.
      // This ensures that even on the first visit to Step 3, we know what the 
      // original business phone was so we can detect edits from Step 2.
      const currentBizPhone = business?.phoneNumber || business?.phone || "";
      if (currentBizPhone && !sessionStorage.getItem("wizard_last_biz_phone")) {
        sessionStorage.setItem("wizard_last_biz_phone", currentBizPhone);
      }

      try {
        sessionStorage.setItem(
          "agentDetails",
          JSON.stringify({ agent, business }),
        );
      } catch { }

      // Sync the global zustand store so downstream consumers (Forward
      // Instructions, ContactsPage, FastAgentDetail) see the same fresh
      // agent/business shape. Same pattern GoLiveWizard uses.
      try {
        useAgentStore
          .getState()
          .setAgentById(agentId, { agent, business }, [], 0);
      } catch (storeErr) {
        console.warn(
          "[PostPurchaseSetup] zustand sync failed (non-fatal):",
          storeErr?.message || storeErr,
        );
      }

      setAgentCtx({
        agentId,
        agent,
        business,
        loading: false,
      });
    } catch (err) {
      console.error("[PostPurchaseSetup] load failed:", err);
      setAgentCtx((c) => ({ ...c, loading: false }));
    }
  };

  // ── Boot guard + resume ──
  useEffect(() => {
    const paidValue = localStorage.getItem("paymentDone");
    const onboardComplete = localStorage.getItem("onboardComplete") === "true";
    const paid = paidValue === "true" || paidValue === true;
    const token = localStorage.getItem("token");
    const savedStep = localStorage.getItem(STORAGE_KEY);

    // Safety: if we expect a Google return, we allow entry even if the transient 'paid' flag is shaky.
    const expectingGoogle = localStorage.getItem("postPurchaseSetup.expectGoogleReturn") === "true";

    console.log("[PostPurchaseSetup] Guard check:", {
      paid,
      paidValue,
      hasToken: !!token,
      expectingGoogle,
      onboardComplete,
      savedStep
    });

    // If we're at the very start, clear any stale sync memory from previous onboarding sessions
    if (!savedStep || savedStep === "1") {
      sessionStorage.removeItem("wizard_last_biz_phone");
    }

    // Relaxed guard: must have token AND (be paid || return from google || started wizard)
    const canEnter = token && (paid || expectingGoogle || !!savedStep);

    if (!canEnter) {
      console.warn("[PostPurchaseSetup] Kick-out triggered: Unauthorized or unpaid entry attempt.");
      navigate("/fast-agent-detail", { replace: true });
      return;
    }

    // Two-signal check for "user actually completed Google OAuth this
    // round" (vs "user clicked browser back from Google's consent page
    // before granting"):
    //   1. localStorage flag set when we initiated OAuth.
    //   2. ?googleAuth=success appended by the backend callback when it
    //      successfully ran. The callback never fires on browser-back,
    //      so the param is absent in that case.
    // Without this gate, a back-cancelled flow would still trigger the
    // tick animation + auto-advance based on a stale prior-session
    // token, pushing the user past Step 1 with permissions they didn't
    // re-authorize this round (and there's no Back button in the
    // wizard footer to recover).
    const urlParams = new URLSearchParams(window.location.search);
    const oauthSuccess = urlParams.get("googleAuth") === "success";
    const returningFromGoogle = expectingGoogle && oauthSuccess;

    if (expectingGoogle && !oauthSuccess) {
      // User cancelled OAuth (browser back from Google). Reset the
      // stale flag so the next entry doesn't false-positive into the
      // success view, and let them see the Grant Access view again.
      localStorage.removeItem("postPurchaseSetup.expectGoogleReturn");
    }

    if (oauthSuccess) {
      // Strip the param after consuming it so a refresh doesn't re-fire
      // the success-view animation indefinitely. Keep any other query
      // params untouched (rare but possible — e.g. utm_*).
      urlParams.delete("googleAuth");
      const remaining = urlParams.toString();
      const cleanUrl =
        window.location.pathname + (remaining ? `?${remaining}` : "");
      window.history.replaceState({}, document.title, cleanUrl);
    }

    (async () => {
      let isAlreadySynced = false;
      // Only hit /calender-check when the visitor JUST came back from
      // the Google OAuth round-trip. On fresh first-time visits we want
      // the user to see the Grant Access view regardless of any prior
      // connection — the wizard treats Step 1 as a fresh start, not as
      // "show existing connection". Without this gate, returning users
      // would see the success view on cold load with no chance to
      // re-grant.
      if (returningFromGoogle) {
        try {
          // Backend requires userId in the body — calenderCheck() without
          // it returns 400 silently which kept isGoogleConnected stuck on
          // false (and the success-view tick render never fired).
          const tokenStr = localStorage.getItem("token");
          const userId = decodeToken(tokenStr)?.id;
          const syncStatusRes = await calenderCheck(userId);
          const connected = !!syncStatusRes?.data?.connected;
          setIsGoogleConnected(connected);
          if (connected && syncStatusRes?.data?.email) {
            setConnectedEmail(syncStatusRes.data.email);
          }
          if (connected && syncStatusRes?.data?.scope) {
            setConnectedScopes(syncStatusRes.data.scope);
          }
        } catch (err) {
          console.error("[PostPurchaseSetup] Sync check failed:", err);
        }
      }

      const savedStr = localStorage.getItem(STORAGE_KEY);
      const saved = parseInt(savedStr, 10);
      let resumeAt = 1;
      if (Number.isFinite(saved) && saved >= 1 && saved <= STEPS.length) {
        resumeAt = saved;
      }

      if (returningFromGoogle || isAlreadySynced) {
        // Earlier this jumped resumeAt to step 2 immediately, which
        // teleported the user past the Google success screen before
        // they could see anything. Now we keep them on step 1 so the
        // GoogleAuthStep can play its tick animation + 2s auto-advance
        // (the step itself calls onNext after the animation finishes).
        // Background calendar provisioning still fires below regardless
        // of which step is visible.
        if (returningFromGoogle) {
          setJustReturnedFromGoogle(true);
        }
        localStorage.removeItem("postPurchaseSetup.expectGoogleReturn");

        // Skip sync popup in future
        isSyncContactSkip().catch((e) => console.error(e));

        // ✅ If we just returned from Google, trigger the "Connect Calendar" (agent specific)
        // logic in the background so Step 3 starts with a connected calendar.
        if (returningFromGoogle && agentCtx?.agentId) {
          const biz = agentCtx?.business || {};
          const ag = agentCtx?.agent || {};
          const calendarName = `REXPT: ${ag.agentName || "Agent"} ${biz.businessName || ""} Meetings`;
          const tz = ag.timezone || "America/New_York";

          console.log("[PostPurchaseSetup] Auto-provisioning agent calendar...");
          axios.post(`${API_BASE_URL}/create-agent-calender`, {
            agentId: ag.agent_id,
            timeZone: tz,
            calendarName: calendarName
          }).then(async () => {
            console.log("[PostPurchaseSetup] Calendar provisioned!");
            // Refresh everything to ensure UI shows "Connected"
            const tokenStr2 = localStorage.getItem("token");
            const userId2 = decodeToken(tokenStr2)?.id;
            const updatedSync = await calenderCheck(userId2);
            setIsGoogleConnected(!!updatedSync?.data?.connected);
            if (updatedSync?.data?.email) setConnectedEmail(updatedSync.data.email);
            if (updatedSync?.data?.scope) setConnectedScopes(updatedSync.data.scope);
            refreshAgentCtx();
          }).catch(e => console.error("[PostPurchaseSetup] Auto-provision failed:", e));

        }
      }
      console.log("[PostPurchaseSetup] Resuming at Step:", resumeAt);
      setStepId(resumeAt);
      await refreshAgentCtx();
      setBootChecked(true);
    })();
  }, [navigate]);

  // ── Persist current step so a refresh resumes here ──
  useEffect(() => {
    if (!bootChecked) return;
    localStorage.setItem(STORAGE_KEY, String(stepId));
  }, [stepId, bootChecked]);

  /*
   * Cascade auto-assign — mirror of GoLiveWizard. Runs at boot when
   * the agent has no voip yet AND we have business location data.
   * Stashes a candidate (no Telnyx order, no DB write) so Step 4's
   * inline picker has something to preview. The actual purchase
   * fires from confirmAndPurchase below.
   */
  useEffect(() => {
    if (!bootChecked) return;
    if (agentCtx?.loading) return;
    const agent = agentCtx?.agent;
    const business = agentCtx?.business;
    const agentId = agent?.agent_id || agentCtx?.agentId;
    if (!agent || !business || !agentId) return;
    if (autoAssignTriedRef.current === agentId) return;

    // Already has a number — skip cascade.
    const rawVoip = agent?.voip_numbers;
    try {
      const parsed = typeof rawVoip === "string" ? JSON.parse(rawVoip) : rawVoip;
      const existing = Array.isArray(parsed) ? parsed[0] : parsed;
      if (existing) {
        autoAssignTriedRef.current = agentId;
        return;
      }
    } catch { /* fall through */ }

    let kb = {};
    try {
      kb =
        typeof business?.knowledge_base_texts === "string"
          ? JSON.parse(business.knowledge_base_texts)
          : business?.knowledge_base_texts || {};
    } catch { kb = {}; }

    const country = (business?.country_code || kb?.country_code || "")
      .toString()
      .toUpperCase();
    const city = business?.city || kb?.city || "";
    const stateCode =
      business?.state_code || kb?.state_code || business?.state || kb?.state || "";

    const isVobiz =
      usesVobiz() ||
      country === "IN" ||
      (isPythonBackend() && !isAppSumoUser());
    const resolvedCountry = isVobiz ? "IN" : country;
    const resolvedCity = isVobiz ? IN_DEFAULT_LOCATION.city : city;
    const resolvedState = isVobiz ? IN_DEFAULT_LOCATION.stateCode : stateCode;

    if (!AUTO_ASSIGN_COUNTRIES.has(resolvedCountry)) {
      autoAssignTriedRef.current = agentId;
      return;
    }

    autoAssignTriedRef.current = agentId;
    setIsAutoAssigning(true);

    (async () => {
      const token = localStorage.getItem("token") || "";
      try {
        let pick = null;
        if (isVobiz) {
          const { fetchAvailableVobizNumbers } = await import("../../Store/apiStore");
          try {
            const res = await fetchAvailableVobizNumbers(
              token,
              "IN",
              resolvedCity,
              resolvedState,
              null,
              null,
            );
            const list = res?.data || [];
            if (list.length > 0) {
              pick = list[0];
            }
          } catch (e) {
            console.error("[auto-assign] Vobiz fetch failed:", e?.message || e);
          }
        } else {
          const attempts = [
            { city, state: stateCode },
            { city: "", state: stateCode },
            { city: "", state: "" },
          ];
          for (const a of attempts) {
            try {
              const res = await fetchAvailablePhoneNumberByCountry(
                token, country, a.city, a.state, null, null,
              );
              const list = res?.data || [];
              if (list.length > 0) { pick = list[0]; break; }
            } catch (e) { console.error("[auto-assign] fetch failed:", e?.message || e); }
          }
        }
        if (!pick) return;
        setCandidate({
          phone_number: pick.phone_number,
          locality: isVobiz ? IN_DEFAULT_LOCATION.city : (pick.locality || pick.city || city || ""),
          region: isVobiz ? IN_DEFAULT_LOCATION.stateCode : (pick.region || pick.state || stateCode || ""),
        });
      } catch (err) {
        console.error("[auto-assign] error:", err?.message || err);
      } finally {
        setIsAutoAssigning(false);
      }
    })();
  }, [agentCtx, bootChecked]);

  if (!bootChecked) {
    return (
      <div className={styles.bootLoader}>
        <span className={styles.spinner} />
      </div>
    );
  }

  const hasExistingNumber = () => {
    try {
      const rawVoip = agentCtx?.agent?.voip_numbers;
      const parsed = typeof rawVoip === "string" ? JSON.parse(rawVoip) : rawVoip;
      const existing = Array.isArray(parsed) ? parsed[0] : parsed;
      return !!existing;
    } catch {
      return false;
    }
  };

  /*
   * confirmAndPurchase — Telnyx order + DB write that commits the
   * candidate (or picker override) to the agent. Same shape as
   * GoLiveWizard's helper. Used by ImmediateOwnershipStep on Step 4
   * "Set Up call Forwarding" tap (default candidate) AND on
   * "Use This Number" tap (picker override).
   */
  const confirmAndPurchase = async (overrideCandidate) => {
    const useCandidate = overrideCandidate?.phone_number ? overrideCandidate : candidate;
    if (!useCandidate?.phone_number) {
      return { success: false, error: "No candidate number to confirm" };
    }
    const agentId = agentCtx?.agent?.agent_id || agentCtx?.agentId;
    if (!agentId) return { success: false, error: "Missing agent ID" };

    if (overrideCandidate?.phone_number) {
      setCandidate(overrideCandidate);
      setEditConsumed(true);
    }
    setConfirming(true);
    setConfirmError("");
    // Gate the success toast on a REAL Telnyx purchase — see the
    // matching comment in GoLiveWizard.confirmAndPurchase for why.
    try {
      const bizCountry = (
        agentCtx?.business?.country_code ||
        agentCtx?.business?.country ||
        ""
      ).toString().toUpperCase();
      const isVobiz =
        usesVobiz() ||
        bizCountry === "IN" ||
        (useCandidate?.phone_number && useCandidate.phone_number.startsWith("+91")) ||
        (isPythonBackend() && !isAppSumoUser());
      const env = process.env.REACT_APP_ENV;
      if (env === "production") {
        if (isVobiz) {
          const { createVobizNumberOrder } = await import("../../Store/apiStore");
          await createVobizNumberOrder(useCandidate.phone_number, agentId);
        } else {
          const { createNumberOrder } = await import("../../Store/apiStore");
          await createNumberOrder(useCandidate.phone_number, agentId);
        }
      }
      await updateAgent(agentId, {
        voip_numbers: [useCandidate.phone_number],
        voip_numbers_created: new Date(),
        voip_number_city: useCandidate.locality || "",
        voip_number_state: useCandidate.region || "",
        assignNumFree: 1,
        isNumActivated: 1,
        isDeactivated: false,
        agentStatus: true,
      });
      markSetupStep("phoneNumberAssigned", agentId);
      // "Number assigned successfully!" toast ka nishaan (CarrierSelectStep ise
      // padh kar toast dikhata hai). Number yahan tak pahunchte hi assign ho chuka
      // hai (upar updateAgent safal), isliye hamesha lagao. Pehle ye sirf tab lagta
      // tha jab asli kharid hui ho (production) — dev/staging me kharid skip hoti
      // hai, to number assign hone par bhi toast nahi aata tha.
      try { sessionStorage.setItem("goLiveWizard.numberAssignedToast", "1"); } catch { }
      await refreshAgentCtx();
      setCandidate(null);
      setConfirming(false);
      return { success: true };
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't reserve this number. Please try picking a different one.";
      setConfirmError(msg);
      setConfirming(false);
      return { success: false, error: msg };
    }
  };

  /*
   * Final exit helper — clears resume key, marks onboardComplete,
   * navigates to dashboard. Used by ContactSyncStep (post-wizard
   * inline render) and SophiaReadyDialog "Go to Dashboard".
   */
  const finishToDashboard = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.setItem("onboardComplete", "true");
    navigate("/fast-agent-detail", { replace: true });
  };

  /*
   * goNext — move to the next step in EFFECTIVE order. We resolve
   * via findIndex(id) instead of arithmetic on stepId because
   * effectiveSteps may skip an id (Personal Details when
   * `?personalDetails=true` is absent), and the original step IDs
   * stay non-contiguous (1, 3, 4, …).
   *
   * Reaching the end of effectiveSteps (i.e. tapping Continue from
   * Triage's SophiaReadyDialog) flips us into the inline ContactSync
   * post-wizard render.
   */
  const goNext = () => {
    const currentIdx = effectiveSteps.findIndex((s) => s.id === stepId);
    const next = effectiveSteps[currentIdx + 1];
    if (next) {
      setStepId(next.id);
    } else {
      // Triage cleared — the wizard is functionally complete. Drop
      // the resume key NOW so a refresh during ContactSyncStep doesn't
      // re-enter the wizard at Step 7 (the user already finished it).
      // onboardComplete is set when ContactSyncStep eventually
      // navigates to /fast-agent-detail.
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch { }
      setShowContactSync(true);
    }
  };

  const goBack = () => {
    const currentIdx = effectiveSteps.findIndex((s) => s.id === stepId);
    if (currentIdx > 0) {
      setStepId(effectiveSteps[currentIdx - 1].id);
    }
  };

  /*
   * Per-step Skip semantics (mirror GoLive):
   *   Step 5 (Carrier) → skip BOTH Carrier + Forwarding, jump to Triage
   *   Step 6 (Forwarding) → handled inside ForwardInstructions itself
   *   Step 7 (Triage) → handled inside TriageStep (opens dialog)
   *   Step 1-4 → no-op (mandatory)
   */
  const goSkip = () => {
    if (stepId === 5) {
      setStepId(7);
      return;
    }
  };

  const current = effectiveSteps.find((s) => s.id === stepId) || effectiveSteps[0];
  const StepComponent = current.Component;
  const onStep4Ownership = stepId === 4;

  /*
   * Post-wizard ContactSync render — replaces the regular step body
   * once the user has cleared Triage's SophiaReadyDialog Continue
   * tap. Same component the dashboard /contacts route uses, with
   * `nextRoute` overridden to /fast-agent-detail (PostPurchase skips
   * the celebration screen — the user has already paid + completed
   * setup, so we land them straight on the dashboard).
   */
  /*
   * Post-wizard ContactSync render — replaces the regular step body
   * once Triage's SophiaReadyDialog Continue lands here. We keep the
   * SAME footer-dots indicator visible so the user sees the wizard
   * still "in progress" through the contact-sync phase. All dots
   * render as completed (the regular wizard work is done by this
   * point — sync is the final post-step).
   */
  /*
   * Post-wizard ContactSync render. Dot bar at bottom shows all
   * effective wizard steps as COMPLETED + an extra "sync" dot as
   * ACTIVE — so the user sees they're now on the contact-sync
   * post-step (not a generic "all done" state). Total dot count =
   * effectiveSteps.length + 1.
   */
  if (showContactSync) {
    console.log(
      "[PostPurchaseSetup DEBUG] ContactSync inline render — dot count =",
      effectiveSteps.length + 1,
      "(wizard steps + sync)",
    );
    return (
      /* Same --wizard-cta-lift var as the main wizard render branch
         below — without it ContactSyncStep's bottomBar would sit at
         bottom: 0 and hide our footer dot bar (also at bottom: 0). */
      <div
        className={styles.wizard}
        style={{ "--wizard-cta-lift": "50px" }}
      >
        <ContactSyncStep nextRoute="/fast-agent-detail" />
        <footer className={styles.footerNav}>
          <div className={styles.footerNavInner}>
            <div className={styles.dots}>
              {effectiveSteps.map((s, index) => (
                <span
                  key={s.id}
                  className={`${styles.dot} ${styles.dotCompleted}`}
                  title={s.label}
                  aria-label={`Step ${index + 1}: ${s.label} (complete)`}
                />
              ))}
              {/* Active "sync" dot — current screen. Number rendered
                  inside via .dotActive's larger size + white text. */}
              <span
                key="sync"
                className={`${styles.dot} ${styles.dotActive}`}
                title="Contact Sync"
                aria-label={`Step ${effectiveSteps.length + 1}: Contact Sync (current)`}
              >
                {effectiveSteps.length + 1}
              </span>
            </div>
          </div>
        </footer>

        <ExitConfirmModal
          open={exitConfirmOpen}
          onStay={() => setExitConfirmOpen(false)}
          onLeave={() => {
            setExitConfirmOpen(false);
            navigate("/fast-agent-detail", { replace: true });
          }}
        />
      </div>
    );
  }

  return (
    /*
     * --wizard-cta-lift = 50px reserves a 50px slice at the viewport
     * bottom for THIS wizard's footer dot bar. Shared step components
     * (SelectCarrier, ImmediateOwnership, ForwardInstructions,
     * CallTransfer, StepShell, ContactSyncStep) in wizardMode read this
     * variable via calc(originalBottom + var(--wizard-cta-lift, 0px))
     * so their fixed CTAs lift up by 50px ONLY when hosted inside
     * PostPurchaseSetup. GoLiveWizard does NOT set this variable, so
     * the same shared components fall back to 0px lift and render at
     * their original bottom offsets there. Clean PostPurchase-only
     * scoping with zero cross-wizard bleed.
     */
    <div
      className={styles.wizard}
      style={{ "--wizard-cta-lift": "50px" }}
    >
      <main
        className={styles.body}
        /*
         * Bottom padding 230px = 50px dot bar + ~170px max CTA shelf
         * height (CallTransfer's full-width shelf + reserve) + small
         * margin. CTAs are lifted up by 50px via the cta-lift var so
         * the bottom dot bar gets its own real estate.
         */
        style={{ paddingBottom: 230 }}
      >
        <StepComponent
          stepId={stepId}
          totalSteps={effectiveSteps.length}
          onNext={goNext}
          onBack={goBack}
          onSkip={goSkip}
          agentCtx={agentCtx}
          refreshAgentCtx={refreshAgentCtx}
          isConnected={isGoogleConnected}
          connectedEmail={connectedEmail}
          connectedScopes={connectedScopes}
          justReturnedFromGoogle={justReturnedFromGoogle}
          /* Candidate / inline-picker flow — only Step 4
             ImmediateOwnershipStep consumes these. Other steps
             ignore unknown props. */
          candidate={candidate}
          isPaidPlan={true}
          confirming={confirming}
          confirmError={confirmError}
          onConfirmPurchase={confirmAndPurchase}
          editConsumed={editConsumed}
          wizardInlinePicker={onStep4Ownership}
          /* Skip-phone-setup escape — only the ownership step (4)
             reads this. Jumps Step 4 → Step 7 (Triage), skipping
             Number / Carrier / Forwarding. Shown only when the user
             is OUTSIDE US/CA (the Telnyx pool's footprint). */
          onSkipPhoneSetup={() => setStepId(7)}
          /* Carrier-step Figma footer (Show me Steps + publish-skip).
             Other steps ignore. */
          wizardSkipPublishDirectly={stepId === 5}
        />
      </main>

      <footer className={styles.footerNav}>
        <div className={styles.footerNavInner}>
          <div className={styles.dots}>
            {effectiveSteps.map((s, index) => {
              const isActive = s.id === stepId;
              const isCompleted = s.id < stepId;
              const displayNum = index + 1;
              return (
                <span
                  key={s.id}
                  className={`${styles.dot} ${isActive ? styles.dotActive : ""} ${isCompleted ? styles.dotCompleted : ""}`}
                  title={s.label}
                  aria-label={`Step ${displayNum}: ${s.label}`}
                >
                  {isActive ? displayNum : ""}
                </span>
              );
            })}
            {/* Final "sync" dot — appears as pending in the regular
                wizard render (user hasn't reached contact-sync yet).
                Once the user passes Triage and the wizard flips
                showContactSync=true, the dedicated render branch
                above takes over and marks this dot as active. */}
            <span
              key="sync"
              className={styles.dot}
              title="Contact Sync"
              aria-label={`Step ${effectiveSteps.length + 1}: Contact Sync`}
            />
          </div>
        </div>
      </footer>

      {/*
         Legacy skip-no-number modal removed — Step 4 now uses the
         merged ImmediateOwnershipStep with inline picker. The picker
         covers the unsupported-region / empty-cascade edge cases via
         Google-search location entry, so the no-number deadlock is
         no longer reachable. SophiaReadyDialog (Triage Step 7) is the
         only remaining pre-dashboard checkpoint, and it has its own
         "Go to Dashboard" exit.
      */}

      <ExitConfirmModal
        open={exitConfirmOpen}
        onStay={() => setExitConfirmOpen(false)}
        onLeave={() => {
          setExitConfirmOpen(false);
          navigate("/fast-agent-detail", { replace: true });
        }}
      />
    </div>
  );
}
